import {
  DOCUMENT,
  inject,
  Injectable,
  Renderer2,
  RendererFactory2,
} from '@angular/core';
import { MediaObserverService } from '@core/services/media-observer.service';
import { environment } from '@environments/environment';
import { ENVIRONMENTS, ViewType } from '@shared/constants';
import { TaggingDAO } from '@shared/interfaces';

type TcEventFn = (
  context: unknown,
  type: 'event',
  payload: Record<string, string>
) => void;

declare const tc_events_3: TcEventFn;
declare const tc_events_25: TcEventFn;

declare global {
  interface Window {
    tC?: { internalvars: { onetrust_id: string } };
    tc_vars?: Record<string, string | number>;
  }
}

const TC_CDN = 'https://cdn.tagcommander.com';
const MINISITES_ACCOUNT = '5008';
const IFRAME_ACCOUNT = '3969';
const CONTAINER_UEX = '20';
const CONTAINER_ANA = '21';
const CONTAINER_MED = '22';
const CONTAINER_IFRAME = '25';

interface ScriptDescriptor {
  src: string;
  parent: HTMLElement;
  isTcContainer: boolean;
}

interface TrackedScript extends ScriptDescriptor {
  loaded: boolean;
  failed: boolean;
  loadPromise?: Promise<void>;
  element?: HTMLScriptElement;
}

interface PendingEmit {
  level2: string;
  level3: string;
  eventName: string;
}

@Injectable({ providedIn: 'root' })
export class CetoineTagging implements TaggingDAO {
  private readonly document = inject(DOCUMENT);
  private readonly rendererFactory = inject(RendererFactory2);
  private readonly mediaObserver = inject(MediaObserverService);

  private renderer: Renderer2 | null = null;
  private scripts: TrackedScript[] = [];
  private pendingEmits: PendingEmit[] = [];

  init(): void {
    // Idempotent: re-running would reset `loaded` / `failed` / `loadPromise`
    // on every TrackedScript, which then re-appends every <script> on the
    // next loadScript call — exactly the EAI-1696 duplicate-script bug.
    if (this.renderer) {
      return;
    }
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.scripts = this.buildScriptList();

    if (environment.env === ENVIRONMENTS.DEV) {
      this.seedOneTrustIdForLocal();
    }
  }

  async loadScript(
    pageName: string,
    pageTemplate: string,
    untienotsId = ''
  ): Promise<void> {
    this.updateTcVars(pageName, pageTemplate, untienotsId);
    await Promise.all([
      this.rerunLoadedContainers(),
      this.loadMissingScripts(),
    ]);
  }

  emitTag(level2: string, level3: string, eventName: string): void {
    if (this.hasFailedScripts()) {
      console.warn('TC scripts failed to load — event dropped:', eventName);
      return;
    }
    if (!this.areScriptsLoaded()) {
      // Race: user interacted before scripts finished loading. Queue and
      // flush on completion so early events aren't silently dropped.
      this.pendingEmits.push({ level2, level3, eventName });
      return;
    }
    this.dispatchEmit(level2, level3, eventName);
  }

  private dispatchEmit(
    level2: string,
    level3: string,
    eventName: string
  ): void {
    const payload: Record<string, string> = {
      event: 'nav',
      click_level1: 'challenge-fid',
      click_level2: level2,
      click_level3: level3,
      event_name: eventName,
    };

    // `typeof` guards against the script loading but failing to define the
    // global (empty body, malformed script, mock). Direct reference would throw.
    if (this.isIframed()) {
      if (typeof tc_events_25 !== 'function') {
        console.warn(
          'tc_events_25 not defined — TC iframe script malformed; event dropped:',
          eventName
        );
        return;
      }
      tc_events_25(this, 'event', payload);
    } else {
      if (typeof tc_events_3 === 'function') {
        tc_events_3(this, 'event', payload);
        return;
      }
      console.warn('tc_events_3 not defined — event dropped:', eventName);
    }
  }

  private flushPendingEmits(): void {
    if (this.pendingEmits.length === 0) {
      return;
    }
    const queue = this.pendingEmits;
    this.pendingEmits = [];
    for (const evt of queue) {
      this.dispatchEmit(evt.level2, evt.level3, evt.eventName);
    }
  }

  private hasFailedScripts(): boolean {
    return this.scripts.some((script: TrackedScript): boolean => script.failed);
  }

  private isIframed(): boolean {
    const win = this.document.defaultView;
    if (!win) {
      return false;
    }
    const fromParam: boolean = new URLSearchParams(win.location.search).has(
      'from_iframe'
    );
    // Cypress runs the AUT inside its own runner iframe, so `top !== self`
    // is always true under test and would force iframe behaviour everywhere.
    // Trust only the URL param under Cypress so specs can opt in explicitly.
    if (environment.env === ENVIRONMENTS.CYPRESS) {
      return fromParam;
    }
    try {
      return win.top !== win.self || fromParam;
    } catch {
      // Cross-origin top access throws — the throw itself confirms framing.
      return true;
    }
  }

  private buildScriptList(): TrackedScript[] {
    const isProd = environment.env === ENVIRONMENTS.PRODUCTION;
    const head = this.document.head;
    const body = this.document.body;

    const jquery: ScriptDescriptor = {
      parent: head,
      src: 'https://code.jquery.com/jquery-3.7.1.min.js',
      isTcContainer: false,
    };

    const descriptors: ScriptDescriptor[] = this.isIframed()
      ? this.buildIframeScripts(isProd, body, jquery)
      : this.buildStandaloneScripts(isProd, head, body, jquery);

    return descriptors.map((descriptor: ScriptDescriptor): TrackedScript => ({
      ...descriptor,
      loaded: false,
      failed: false,
    }));
  }

  private buildIframeScripts(
    isProd: boolean,
    body: HTMLElement,
    jquery: ScriptDescriptor
  ): ScriptDescriptor[] {
    const base = `${TC_CDN}/${IFRAME_ACCOUNT}${isProd ? '' : '/uat'}`;
    return [
      jquery,
      {
        src: `${base}/tc_CarrefourONE_${CONTAINER_IFRAME}.js`,
        parent: body,
        isTcContainer: true,
      },
    ];
  }

  private buildStandaloneScripts(
    isProd: boolean,
    head: HTMLElement,
    body: HTMLElement,
    jquery: ScriptDescriptor
  ): ScriptDescriptor[] {
    const base = `${TC_CDN}/${MINISITES_ACCOUNT}${isProd ? '' : '/uat'}`;
    return [
      jquery,
      {
        src: `${base}/tc_CarrefourMiniSites_${CONTAINER_UEX}.js`,
        parent: head,
        isTcContainer: true,
      },
      {
        src: `${base}/tc_CarrefourMiniSites_${CONTAINER_ANA}.js`,
        parent: body,
        isTcContainer: true,
      },
      {
        src: `${base}/tc_CarrefourMiniSites_${CONTAINER_MED}.js`,
        parent: body,
        isTcContainer: true,
      },
    ];
  }

  private async loadMissingScripts(): Promise<void> {
    try {
      await Promise.all(
        this.scripts.map((script: TrackedScript): Promise<void> =>
          this.ensureScriptLoaded(script)
        )
      );
      this.flushPendingEmits();
    } catch (error) {
      // Scripts failed: drop queued events so they don't leak memory or
      // fire against a partial/missing TC runtime.
      this.pendingEmits = [];
      throw error;
    }
  }

  // Page tags (GA4 - Pageview) live in the container body, so TC fires them
  // only when a container executes; tC.container.reload() does not replay
  // them. Every container that already ran therefore runs again for the new
  // page — same src, so the browser serves it from its HTTP cache.
  private async rerunLoadedContainers(): Promise<void> {
    const loadedContainers = this.scripts.filter(
      (script: TrackedScript): boolean => script.isTcContainer && script.loaded
    );
    await Promise.all(
      loadedContainers.map((script: TrackedScript): Promise<void> =>
        this.runScript(script)
      )
    );
  }

  private ensureScriptLoaded(script: TrackedScript): Promise<void> {
    if (script.loaded || script.failed) {
      return Promise.resolve();
    }
    if (script.loadPromise) {
      return script.loadPromise;
    }

    script.loadPromise = this.runScript(script).then(
      (): void => {
        script.loaded = true;
      },
      (error: Error): never => {
        script.failed = true;
        throw error;
      }
    );

    return script.loadPromise;
  }

  // A <script> element executes only once, so each run needs a fresh one;
  // the previous element goes, keeping a single tag per script in the DOM.
  private runScript(script: TrackedScript): Promise<void> {
    return new Promise<void>(
      (resolve: () => void, reject: (reason: Error) => void): void => {
        const renderer: Renderer2 | null = this.renderer;
        if (!renderer) {
          reject(new Error('CetoineTagging not initialised'));
          return;
        }
        if (script.element) {
          renderer.removeChild(script.parent, script.element);
        }
        const element: HTMLScriptElement = renderer.createElement('script');
        element.type = 'text/javascript';
        element.async = true;
        element.src = script.src;
        element.onload = (): void => resolve();
        element.onerror = (): void => {
          renderer.removeChild(script.parent, element);
          reject(new Error(`Failed to load tagging script: ${script.src}`));
        };
        script.element = element;
        renderer.appendChild(script.parent, element);
      }
    );
  }

  private updateTcVars(
    pageName: string,
    pageTemplate: string,
    untienotsId: string
  ): void {
    const win = this.document.defaultView;
    if (!win) {
      return;
    }
    win.tc_vars = {
      env_template: 'Carrefour',
      env_site: 'CHF',
      env_work:
        environment.env === ENVIRONMENTS.PRODUCTION ? 'PROD' : 'PREPROD',
      env_device: this.mediaObserver.device(),
      env_variation: this.getViewType(),
      page_name: pageName,
      page_template: pageTemplate,
      ...(untienotsId ? { user_id: untienotsId } : {}),
    };
  }

  private getViewType(): ViewType {
    const search: string = this.document.defaultView?.location.search ?? '';
    const params = new URLSearchParams(search);
    return params.get('env_variation') === ViewType.WEBVIEW
      ? ViewType.WEBVIEW
      : ViewType.WEB;
  }

  private areScriptsLoaded(): boolean {
    return (
      this.scripts.length > 0 &&
      this.scripts.every((script: TrackedScript): boolean => script.loaded)
    );
  }

  // Only used to test the OneTrust banner against the test container locally.
  private seedOneTrustIdForLocal(): void {
    const win = this.document.defaultView;
    if (win && !win.tC) {
      win.tC = {
        internalvars: {
          onetrust_id: '97cb676a-2ac9-40a1-98f9-ad72fa5847e5-test',
        },
      };
    }
  }
}
